import { Sheet } from '../ui/Sheet.jsx';
import { Button } from '../ui/Button.jsx';
import { EmptyState } from '../ui/EmptyState.jsx';
import { Spinner } from '../ui/Spinner.jsx';
import { CartLine } from './CartLine.jsx';
import { CouponField } from './CouponField.jsx';
import { OrderSummary } from './OrderSummary.jsx';
import { useCart } from '../../features/cart/useCart.js';
import { useUi } from '../../stores/ui.js';
import { formatMoney } from '../../lib/format.js';

/**
 * The bag as a slide-over. Adding something opens it rather than navigating
 * away, so a shopper mid-browse keeps their place — which is the whole reason
 * the panel exists rather than only the page.
 */

export const CartSheet = () => {
  const open = useUi((state) => state.cartOpen);
  const close = useUi((state) => state.closeCart);

  const { data: cart, isPending } = useCart();

  const isEmpty = !isPending && (cart?.items?.length ?? 0) === 0;

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Your bag"
      description={cart?.itemCount ? `${cart.itemCount} in the bag` : undefined}
      footer={
        !isEmpty && cart ? (
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-medium tracking-[0.08em] text-ink uppercase">Total</span>
              <span className="text-lg font-medium text-brand" data-numeric>
                {formatMoney(cart.pricing.total)}
              </span>
            </div>

            <Button to="/checkout" size="lg" fullWidth onClick={close}>
              Checkout
            </Button>

            <button
              type="button"
              onClick={close}
              className="w-full text-center text-2xs text-muted underline decoration-mist underline-offset-4 transition-colors hover:text-brand hover:decoration-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              Keep shopping
            </button>
          </div>
        ) : undefined
      }
    >
      {isPending ? (
        <div className="grid place-items-center py-20">
          <Spinner className="size-5 text-muted" label="Loading your bag" />
        </div>
      ) : isEmpty ? (
        <EmptyState
          title="Your bag is empty"
          description="Every product comes with its full specification on the page. Somewhere in there is the one you want."
          action={{ label: 'Browse fragrance', to: '/shop?world=fragrance' }}
          secondaryAction={{ label: 'Browse sport', to: '/shop?world=sport' }}
        />
      ) : (
        <div className="px-5">
          <ul className="divide-y divide-mist">
            {cart.items.map((line) => (
              <CartLine key={line.id} line={line} compact />
            ))}
          </ul>

          <div className="border-t border-mist py-5">
            <CouponField cart={cart} />
          </div>

          <div className="border-t border-mist py-5">
            <OrderSummary
              pricing={cart.pricing}
              coupon={cart.coupon}
              shipping={cart.shipping}
              itemCount={cart.itemCount}
            />
          </div>
        </div>
      )}
    </Sheet>
  );
};

export default CartSheet;
